import { publicExportText, sanitizePublicAwardsRows } from "./publicMatchExportData";

const clean = value => publicExportText(value).trim();
const courseKey = value => clean(value).toLowerCase().replace(/[^a-z0-9]/g, "");
const courseLabels = { slowfire: "SLOW", nationalmatchcourse: "NMC", timedfire: "TF", rapidfire: "RF" };
const courseOrder = ["slowfire", "nationalmatchcourse", "timedfire", "rapidfire"];
const hasScore = source => [source?.shotsFired, source?.totalScore, source?.xCount].some(value => Number(value) > 0);

function firearmLabel(title) {
  const key = courseKey(title);
  if (["rimfire", "22", "22caliber", "22cal"].includes(key)) return "22";
  if (["centerfire", "centrefire", "cf"].includes(key)) return "CF";
  if (["45", "45caliber", "45cal"].includes(key)) return "45";
  return clean(title) || "Firearm";
}

function scoreValue(source, metric) {
  if (!hasScore(source)) return "";
  if (metric === "scoreAndX") return `${scoreValue(source, "totalScore")}-${scoreValue(source, "xCount")}X`;
  const raw = source?.[metric];
  if (raw == null || raw === "" || !Number.isFinite(Number(raw)) || Number(raw) < 0) {
    throw new Error("A recorded score is incomplete. Refresh the results before exporting.");
  }
  return Number(raw);
}

export function buildBullseyeAwardsWorksheet(rows = [], metric = "scoreAndX") {
  rows = sanitizePublicAwardsRows(rows);
  if (!["scoreAndX", "totalScore", "xCount"].includes(metric)) throw new Error("Unknown worksheet score type.");
  if (!rows.length) throw new Error("No competitors are available to export.");
  const groups = new Map();
  for (const row of rows) {
    const seenGroups = new Set();
    if (hasScore(row) && !row.firearms?.length) {
      throw new Error("Course scores are unavailable. Refresh the detailed results before exporting.");
    }
    for (const firearm of row.firearms || []) {
      const id = clean(firearm.gunGroupId);
      if (!id || seenGroups.has(id)) throw new Error("Firearm score groups are ambiguous. Refresh the results before exporting.");
      seenGroups.add(id);
      const group = groups.get(id) || { id, title: firearmLabel(firearm.firearmTitle), courses: new Map() };
      const seenCourses = new Set();
      for (const course of firearm.courses || []) {
        const key = courseKey(course.courseName);
        if (!key || seenCourses.has(key)) throw new Error("Course scores are ambiguous. Refresh the results before exporting.");
        seenCourses.add(key);
        group.courses.set(key, courseLabels[key] || clean(course.courseName));
      }
      if (hasScore(firearm) && !seenCourses.size) throw new Error("Course scores are unavailable. Refresh the detailed results before exporting.");
      groups.set(id, group);
    }
  }
  if (!groups.size) throw new Error("No course scores are available to export.");

  // Use firearm identity, never competitor equipment choice, to keep .22-only
  // entries in the correct course columns. Repeated/custom groups stay separate.
  const ordered = [...groups.values()].sort((a, b) => {
    const index = title => ({ "22": 0, CF: 1, "45": 2 }[title] ?? 3);
    return index(a.title) - index(b.title);
  });
  for (const group of ordered) {
    group.label = ordered.filter(other => other.title === group.title).length > 1
      ? `${group.title} ${ordered.filter(other => other.title === group.title).indexOf(group) + 1}` : group.title;
    group.courses = [...group.courses].sort(([a], [b]) => {
      const index = key => courseOrder.includes(key) ? courseOrder.indexOf(key) : 4;
      return index(a) - index(b);
    });
  }
  const headers = ["NAME", ...ordered.flatMap(group => [
    ...group.courses.map(([, label]) => `${group.label} ${label}`), `${group.label} AGG`,
  ]), "GRAND AGG"];
  const values = [...rows].sort((a, b) => clean(a.competitorName).localeCompare(clean(b.competitorName))).map(row => {
    const cells = ordered.flatMap(group => {
      const firearm = row.firearms?.find(item => clean(item.gunGroupId) === group.id);
      return [...group.courses.map(([key]) => scoreValue(firearm?.courses?.find(course => courseKey(course.courseName) === key), metric)),
        scoreValue(firearm, metric)];
    });
    return [clean(row.competitorName) || "Competitor", ...cells, scoreValue(row, metric)];
  });
  return { headers, rows: values };
}

function csvCell(value) {
  if (typeof value === "number") return String(value);
  const text = String(value ?? "").replace(/\r\n|\r|\n/g, "\r\n");
  const safe = /^[\s\uFEFF]*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export function createBullseyeAwardsCsv({ rows, metric = "scoreAndX" }) {
  const worksheet = buildBullseyeAwardsWorksheet(rows, metric);
  return "\uFEFF" + [worksheet.headers, ...worksheet.rows].map(row => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

export function downloadBullseyeAwardsCsv({ matchName, rows, metric = "scoreAndX" }) {
  const csv = createBullseyeAwardsCsv({ rows, metric });
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const filename = clean(matchName).normalize("NFKD").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 100) || "bullseye-match";
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${filename}-${metric === "xCount" ? "x-counts" : "scores"}.csv`;
  document.body.appendChild(anchor);
  try { anchor.click(); }
  finally {
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
